import { useEffect, useMemo, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/ui/empty-state";
import {
  AlertCircleIcon,
  ArrowLeft,
  ArrowRight,
  FileInputIcon,
  FileQuestionIcon,
} from "lucide-react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { useDashboardStore } from "@/stores/dashboard/dashboardStore";
import { useBriefStore } from "@/stores";
import { SelectList, SelectListItem } from "@/components/ui/select-list";
import { ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";

interface ImportBriefDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCampaignId: string;
}

export function ImportBriefDialog({
  open,
  onOpenChange,
  currentCampaignId,
}: ImportBriefDialogProps) {
  const { campaigns, campaignsLoading } = useDashboardStore();
  const { copyBriefFrom } = useBriefStore();
  const { getToken } = useAuth();

  const [step, setStep] = useState<"pick" | "confirm">("pick");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setSubmitting(false);
      setStep("pick");
    }
  }, [open]);

  const otherCampaigns = useMemo(
    () =>
      (campaigns ?? [])
        .filter(
          (c) => c?._id && c._id !== currentCampaignId && c.status !== "POPULATING"
        )
        .sort((a, b) => {
          const ad = new Date(a.creation_date ?? 0).getTime();
          const bd = new Date(b.creation_date ?? 0).getTime();
          return bd - ad;
        }),
    [campaigns, currentCampaignId]
  );

  const selectedCampaign = useMemo(
    () => otherCampaigns.find((c) => c._id === selectedId) ?? null,
    [otherCampaigns, selectedId]
  );

  const handleConfirm = async () => {
    if (!selectedId || !currentCampaignId) return;
    setSubmitting(true);
    const token = (await getToken()) || "";
    const ok = await copyBriefFrom(selectedId, currentCampaignId, token);
    setSubmitting(false);
    if (ok) {
      toast.success("Brief imported");
      onOpenChange(false);
    } else {
      toast.error("Failed to import brief");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        {step === "pick" && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Import brief from another campaign</AlertDialogTitle>
              <AlertDialogDescription>
                Pick a campaign to copy its brief from.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {campaignsLoading ? (
              <div className="flex items-center justify-center py-10">
                <Spinner className="size-5 text-muted-foreground" />
              </div>
            ) : otherCampaigns.length === 0 ? (
              <div className="w-full flex items-center justify-center py-6">
                <EmptyState icon={<FileQuestionIcon />} text="No other campaigns yet" />
              </div>
            ) : (
              <SelectList
                value={selectedId ?? ""}
                onValueChange={setSelectedId}
                className="max-h-100"
              >
                {otherCampaigns.map((c) => (
                  <SelectListItem key={c._id} value={c._id}>
                    <ItemContent className="min-w-0 gap-0">
                      <ItemTitle className="truncate">
                        {c.campaign_name || "Untitled campaign"}
                      </ItemTitle>
                      {c.platform && (
                        <ItemDescription className="capitalize">
                          {c.platform}
                        </ItemDescription>
                      )}
                    </ItemContent>
                  </SelectListItem>
                ))}
              </SelectList>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <Button
                onClick={() => setStep("confirm")}
                disabled={!selectedId}
                variant="neutral"
              >
                Next
                <ArrowRight />
              </Button>
            </AlertDialogFooter>
          </>
        )}

        {step === "confirm" && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Replace current brief?</AlertDialogTitle>
              <AlertDialogDescription>
                The brief from{" "}
                <span className="font-medium text-foreground">
                  {selectedCampaign?.campaign_name || "the selected campaign"}
                </span>{" "}
                will replace your current brief.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Alert variant="warning">
              <AlertTitle>
                <AlertCircleIcon />
                Your current brief will be replaced.
              </AlertTitle>
              <AlertDescription>This action cannot be undone.</AlertDescription>
            </Alert>
            <AlertDialogFooter>
              <Button
                variant="secondary"
                onClick={() => setStep("pick")}
                disabled={submitting}
              >
                <ArrowLeft />
                Back
              </Button>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirm();
                }}
                variant="neutral"
                disabled={submitting}
              >
                {submitting && <Spinner />}
                {!submitting && <FileInputIcon />}
                Replace Brief
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
